import type { ValueRef } from "./authDescriptor";

/**
 * `app.config.X` returns a ValueRef marker whose non-enumerable `toString`
 * emits the proxy's substitution token, so `` `${app.config.X}/api` ``
 * naturally produces the template string "{configuration.X}/api".
 */
type RefAccessor<Fields extends string, Kind extends "config" | "secret"> = {
  readonly [K in Fields]: { from: Kind; field: K };
};

export type AuthContext<Cfg extends string, Sec extends string> = {
  config: RefAccessor<Cfg, "config">;
  secret: RefAccessor<Sec, "secret">;
  oauthToken: {
    readonly accessToken: ValueRef;
    readonly tokenType: ValueRef;
  };
};

function marker(
  kind: "config" | "secret" | "oauth",
  field: string
): ValueRef & { toString(): string } {
  const obj = { from: kind, field } as ValueRef & { toString(): string };
  const token =
    kind === "config"
      ? `{configuration.${field}}`
      : kind === "secret"
        ? `{secrets.${field}}`
        : `{oauth.${field}}`;
  Object.defineProperty(obj, "toString", {
    value: () => token,
    enumerable: false,
  });
  return obj;
}

const refProxy = (kind: "config" | "secret") =>
  new Proxy(
    {},
    {
      get: (_t, field) =>
        typeof field === "string" ? marker(kind, field) : undefined,
    }
  );

const sharedContext = {
  config: refProxy("config"),
  secret: refProxy("secret"),
  oauthToken: {
    accessToken: marker("oauth", "accessToken"),
    tokenType: marker("oauth", "tokenType"),
  },
};

export function getAuthContext<
  Cfg extends string,
  Sec extends string,
>(): AuthContext<Cfg, Sec> {
  return sharedContext as unknown as AuthContext<Cfg, Sec>;
}

export function coerceValueRef(value: string | ValueRef): ValueRef {
  if (typeof value !== "string") return value;
  return /\{(configuration|secrets|oauth)\.\w+\}/.test(value)
    ? { from: "template", template: value }
    : { from: "literal", value };
}
